/**
 * Sort de la place d'une demande de consentement si on la supprimait
 * maintenant (DEV-621, boîtes de suppression M4, recette du 15/10).
 *
 * INTENTION. Hors liste de facturation, la boîte qui supprime une demande dit
 * aussi, quand le serveur sait le dire, ce que devient la place : « Elle reste
 * facturée jusqu'au JJ/MM, fin de la période en cours ; sa place sera libérée
 * le JJ/MM. » ou « Sa place sera libérée dès la suppression. ». Mêmes mots que
 * la liste de facturation (`phrasePlace`). Réponse nulle, échec, 404 ou 403 :
 * aucune phrase, la boîte reste comme avant, sans message d'erreur.
 */
import type { Services } from "@services"
import { phraseReponseDemande } from "@modules/espace-entreprise/components/company/billing/billingSourceDemande"

/** Attente maximale de la phrase avant d'ouvrir la boîte sans elle (revue de #806) */
export const DELAI_PHRASE_PLACE_MS = 4000

/**
 * Lit, par l'appel ciblé, la phrase sur la place de la demande.
 * @param services - services injectés (appel absent : `null`)
 * @param idDemande - identifiant de la demande
 * @param delaiMs - attente maximale avant d'ouvrir la boîte sans phrase
 * @returns la phrase, ou `null` si rien n'est promis (ou hors délai)
 */
export async function lirePhrasePlaceDemande(
    services: Partial<Services> | undefined | null,
    idDemande: string | null | undefined,
    delaiMs: number = DELAI_PHRASE_PLACE_MS
): Promise<string | null> {
    if (!idDemande || !services?.$getConsentRequestBillingSiSupprimee) {
        return null
    }
    let minuterie: ReturnType<typeof setTimeout> | undefined
    try {
        // Borne de la boîte : au-delà, elle s'ouvre sans phrase
        const reponse = await Promise.race([
            services.$getConsentRequestBillingSiSupprimee(idDemande),
            new Promise<null>((resolve) => {
                minuterie = setTimeout(() => resolve(null), delaiMs)
            }),
        ])
        return phraseReponseDemande(reponse)
    } catch {
        return null
    } finally {
        clearTimeout(minuterie)
    }
}
